import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { questionnaireRoutes } from './modules/questionnaire/routes';
import { adminRoutes } from './modules/admin/routes';
import ContractReviewGate from './modules/contract_review/components/ContractReviewGate';
import ContractReviewHomePage from './modules/contract_review/components/ContractReviewHomePage';
import ContractUpload from './modules/contract_review/components/ContractUpload';
import ClausePasteUpload from './modules/contract_review/components/ClausePasteUpload';
import ContractReviewListPage from './modules/contract_review/components/ContractReviewListPage';
import ContractReviewSessionPage from './modules/contract_review/components/ContractReviewSessionPage';
import RiskManagementRedirect from './modules/risk_management/RiskManagementRedirect';
import BaselineSecurityRedirect from './modules/baseline_security/BaselineSecurityRedirect';
import SecurityIncidentRedirect from './modules/security_incident/SecurityIncidentRedirect';
import ThreatHuntRedirect from './modules/threat_hunt/ThreatHuntRedirect';
import { HomePage } from './shared/components';
import AccessErrorPage from './shared/components/AccessErrorPage';
import AppNavigationBridge from './shared/components/AppNavigationBridge';
import AuthRoute from './shared/components/AuthRoute';
import Login from './Login';
import Callback from './Callback';
import { isContractReviewEnabled, isIrmtEnabled, isIrisEnabled, isBaselineSecurityEnabled, isThreatHuntEnabled } from './shared/utils/features';
import { MeProvider } from './shared/context/MeContext';
import { SidebarChromeProvider } from './shared/context/SidebarChromeContext';
import { refreshIfNeeded } from './shared/auth/okta';

/** Pathless layout: one MeProvider for all authenticated routes (single GET /api/me). */
function AuthenticatedShell() {
  // Refresh the access token when the user returns to this tab after being away.
  // This covers the case where the tab was backgrounded and the timer-based approach
  // would have been throttled by the browser.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <MeProvider>
      <SidebarChromeProvider>
        <AppNavigationBridge />
        <Outlet />
      </SidebarChromeProvider>
    </MeProvider>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#48a1ad',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Bitter", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700,
    },
    h2: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    h3: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    h4: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    h5: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    h6: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    body1: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    body2: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    button: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
  },
});

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login/callback" element={<Callback />} />
      <Route element={<AuthRoute requireAuth={false} />}>
        <Route path="/login" element={<Login />} />
      </Route>
      <Route element={<AuthRoute requireAuth={true} />}>
        <Route element={<AuthenticatedShell />}>
          <Route path="/" element={<HomePage />} />
          {questionnaireRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {isContractReviewEnabled() && (
            <Route path="contract-review" element={<ContractReviewGate />}>
              <Route index element={<ContractReviewHomePage />} />
              <Route path="upload" element={<ContractUpload />} />
              <Route path="paste" element={<ClausePasteUpload />} />
              <Route path="review" element={<ContractReviewListPage />} />
              <Route path=":sessionUuid" element={<ContractReviewSessionPage />} />
            </Route>
          )}
          {isIrmtEnabled() && (
            <Route path="risk-management" element={<RiskManagementRedirect />} />
          )}
          {isIrisEnabled() && (
            <Route path="security-incident" element={<SecurityIncidentRedirect />} />
          )}
          {isThreatHuntEnabled() && (
            <Route path="threat-hunt" element={<ThreatHuntRedirect />} />
          )}
          {isBaselineSecurityEnabled() && (
            <Route path="baseline-security" element={<BaselineSecurityRedirect />} />
          )}
          {adminRoutes}
          <Route path="/forbidden" element={<AccessErrorPage variant="forbidden" />} />
          <Route path="/not-found" element={<AccessErrorPage variant="not-found" />} />
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
          <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
